/* lp-comparison.js — the comparison table, made live: a 3×3 grid beside a preview panel.
   Ported 9/16 Sep 2026 from the prototype's CMP IIFE (grep "the comparison table, made live" in
   website/Leatherpress_Website_v4_branch2.html). Behaviour identical:

   · hover or click a column HEADING → that collection is active: the others dim, the gold underline
     moves, and the panel shows the collection's banner, eyebrow, title, blurb and price.
   · hover or tap any data CELL (Luke, 25 Aug: "view across the grid and have the image update and
     the column highlight accordingly") → the active column follows the cell AND the panel shows that
     cell's own photograph and caption. The collection's identity (eyebrow/title/price) always follows
     the active column, even mid-hover; only the caption and frame belong to the cell.
   · leaving the TABLE (not the row — moving cell to cell must not flash the preview back) restores
     the collection preview.
   · photographs are lazy <img> layers created on first use and cross-faded with .on.

   Data comes from the section's markup (data- attributes on th/td) and a JSON block for the column
   meta, so a design team edits it in the editor. CMPGRIDHOVER=false restores the old row-level
   binding exactly; it is the `grid_hover` setting.

   One change from the prototype (site review, 25 Sep 2026: "When my mouse is hovering over the
   artisan or heritage column if I decide to move to click on see the collection it changes to the
   Inspire"): Inspire is the column next to the panel, so the pointer crossed it on the way to "see
   the collection". While the pointer is travelling towards the panel, a change of column now waits —
   the mega-menu "aim" test — and lands only if the pointer comes to rest in the new column
   (AIM_REST ms); it is dropped when the pointer leaves the table. Pointing within the active
   column, pointing away from the panel, clicks and taps act at once, as before. Fine pointers only. */
(function () {
  'use strict';
  if (!('customElements' in window) || customElements.get('lp-comparison')) return;

  class LPComparison extends HTMLElement {
    connectedCallback() {
      this.disconnectedCallback();
      var self = this;
      var table = this.querySelector('table[data-cmp-table]');
      var shot = this.querySelector('[data-cmp-shot]');
      var meta = this.querySelector('script[type="application/json"][data-cmp-cols]');
      if (!table || !shot || !meta) return;
      var COLS; try { COLS = JSON.parse(meta.textContent) || []; } catch (e) { COLS = []; }
      if (!COLS.length) return;
      var gridHover = this.getAttribute('data-grid-hover') !== 'false';
      var el = function (k) { return self.querySelector('[data-cmp-' + k + ']'); };
      var eyebrow = el('eyebrow'), title = el('title'), blurb = el('blurb'), price = el('price'), go = el('go');

      shot.innerHTML = '';
      var imgs = {};
      function layer(src) {
        if (imgs[src]) return imgs[src];
        var i = document.createElement('img'); i.loading = 'lazy'; i.alt = ''; i.src = src;
        shot.appendChild(i); imgs[src] = i; return i;
      }
      function show(src) {
        if (!src) return;
        var e = layer(src);
        /* a frame's grace so a freshly created <img> transitions in rather than appearing */
        requestAnimationFrame(function () { for (var k in imgs) imgs[k].classList.toggle('on', imgs[k] === e); });
      }
      var ths = Array.prototype.slice.call(table.querySelectorAll('th[data-col]'));
      var rows = Array.prototype.slice.call(table.querySelectorAll('tr[data-row]'));
      var active = 0, hovering = false;

      var panel = this.querySelector('.cmppanel');
      var AIM_REST = 300, AIM_SLACK = 40;
      var fine = !!(window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches);
      var trail = [], held = null;
      /* is the pointer's line of travel (from a few moves ago to now) heading into the panel? */
      function aimingAtPanel(e) {
        if (!fine || !panel || !trail.length || e == null || e.clientX == null) return false;
        var a = trail[0], bx = e.clientX, by = e.clientY;
        var r = panel.getBoundingClientRect(); if (!r.width || !r.height) return false;
        var edge = bx < r.left ? r.left : (bx > r.right ? r.right : null);
        if (edge === null) return false;                           /* level with the panel already */
        var dx = bx - a.x; if (!dx || (edge - bx) * dx < 0) return false;   /* still, or heading away */
        var y = by + (by - a.y) * (edge - bx) / dx;                /* where that line meets the panel's near edge */
        return y >= r.top - AIM_SLACK && y <= r.bottom + AIM_SLACK;
      }
      function release() { if (held) { clearTimeout(held.t); held = null; } }
      function hold(fn) { release(); held = { fn: fn, t: setTimeout(function () { var f = held && held.fn; held = null; if (f) f(); }, AIM_REST) }; }
      /* a column change happens now, or waits while the pointer heads for the panel */
      function toColumn(col, e, fn) {
        if (col !== active && aimingAtPanel(e)) { hold(fn); return; }
        release(); fn();
      }

      function paint() {
        var d = COLS[active];
        ths.forEach(function (th, i) { th.classList.toggle('on', i === active); th.classList.toggle('dim', i !== active); });
        rows.forEach(function (r) {
          Array.prototype.slice.call(r.children).forEach(function (td, i) {
            if (i === 0) return;                          /* the row label never dims */
            td.classList.toggle('dim', (i - 1) !== active);
          });
        });
        if (eyebrow) eyebrow.textContent = d.eyebrow || '';
        if (title) title.textContent = d.title || '';
        if (price) price.textContent = d.price || '';
        if (go) go.setAttribute('href', d.url || '#');
        if (hovering) return;                             /* a cell is showing its own photograph — leave it */
        if (blurb) blurb.textContent = d.blurb || '';
        show(d.img);
      }
      function showCell(td, col) {
        if (col >= 0) active = col;                       /* col<0 is the row label: it belongs to no column */
        var img = td.getAttribute('data-img'), cap = td.getAttribute('data-cap');
        if (!img) return;
        hovering = true;
        paint();
        if (blurb) blurb.textContent = cap || '';
        show(img);
      }
      var unbind = [release];
      function on(target, ev, fn) { target.addEventListener(ev, fn); unbind.push(function () { target.removeEventListener(ev, fn); }); }

      on(this, 'mousemove', function (e) {
        trail.push({ x: e.clientX, y: e.clientY }); if (trail.length > 4) trail.shift();
        if (!held) return;
        if (aimingAtPanel(e)) hold(held.fn);                       /* still travelling: wait for it to rest */
        else { var f = held.fn; release(); f(); }                  /* turned away from the panel: land now */
      });

      ths.forEach(function (th, i) {
        /* a heading is not a cell, so it always returns the panel to the collection preview */
        on(th, 'click', function () { release(); active = i; hovering = false; paint(); });
        on(th, 'mouseenter', function (e) { toColumn(i, e, function () { active = i; hovering = false; paint(); }); });
      });
      rows.forEach(function (r) {
        var cells = Array.prototype.slice.call(r.children);
        if (gridHover) {
          cells.forEach(function (td, i) {
            if (i === 0) return;
            on(td, 'mouseenter', function (e) { toColumn(i - 1, e, function () { showCell(td, i - 1); }); });
            /* touch has no hover: a tap does what pointing does */
            on(td, 'click', function () { release(); showCell(td, i - 1); });
          });
        } else {
          on(r, 'mouseenter', function () { var td = cells[active + 1]; if (td) showCell(td, -1); });
          on(r, 'mouseleave', function () { hovering = false; paint(); });
        }
      });
      on(table, 'mouseleave', function () { release(); if (gridHover) { hovering = false; paint(); } });
      this._unbind = function () { unbind.forEach(function (f) { f(); }); };
      paint();
    }
    disconnectedCallback() { if (this._unbind) { this._unbind(); this._unbind = null; } }
  }
  customElements.define('lp-comparison', LPComparison);
})();
